import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, TextField, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { Add, Refresh } from "@material-ui/icons";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import api from "../../services/api";
import { contactPresentation, errorMessage, formatDateTime, localDeadlineToISO, mergePages } from "./helpers";
import useStyles from "./styles";

const types = { followup: "Retorno", call: "Ligação", meeting: "Reunião" };
const statuses = { open: "Aberto", completed: "Concluído", cancelled: "Cancelado" };
const views = { open: "Todos os abertos", today: "Hoje", overdue: "Atrasados", upcoming: "Próximos", completed: "Concluídos", cancelled: "Cancelados" };

export function TaskForm({ contactId, contactName, onClose, onCreated }) {
  const classes = useStyles();
  const [owners, setOwners] = useState([]);
  const [ownerLoading, setOwnerLoading] = useState(true);
  const [ownerAttempt, setOwnerAttempt] = useState(0);
  const [form, setForm] = useState({ title: "", description: "", type: "followup", ownerId: "", dueAt: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(uuidv4());
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  useEffect(() => {
    let active = true;
    setOwnerLoading(true);
    setError("");
    api.get("/crm/tasks/owners").then(({ data }) => {
      if (!active) return;
      setOwners(data);
      if (data.length === 1) setForm(previous => ({ ...previous, ownerId: data[0].id }));
    }).catch(err => active && setError(errorMessage(err))).finally(() => active && setOwnerLoading(false));
    return () => { active = false; };
  }, [ownerAttempt]);
  const change = key => event => {
    requestId.current = uuidv4();
    setForm({ ...form, [key]: event.target.value });
  };
  const submit = async event => {
    event.preventDefault();
    if (saving) return;
    setError("");
    let dueAt;
    try { dueAt = localDeadlineToISO(form.dueAt); } catch (err) { setError(err.message); return; }
    if (!form.title.trim() || !form.ownerId) { setError("Informe um título e um responsável."); return; }
    setSaving(true);
    try {
      await api.post(`/crm/contacts/${contactId}/tasks`, { ...form, title: form.title.trim(), dueAt, ownerId: Number(form.ownerId), requestId: requestId.current });
      if (!alive.current) return;
      toast.success("Retorno criado.");
      onCreated();
    } catch (err) { if (alive.current) setError(errorMessage(err)); } finally { if (alive.current) setSaving(false); }
  };
  return <Dialog open onClose={() => !saving && onClose()} fullWidth maxWidth="sm" aria-labelledby="crm-new-task-title">
    <form onSubmit={submit}>
      <DialogTitle id="crm-new-task-title">Novo retorno · {contactName}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" className={classes.error}>{error}{!owners.length && !ownerLoading && <Button onClick={() => setOwnerAttempt(value => value + 1)}>Tentar carregar responsáveis</Button>}</Alert>}
        <div className={classes.dialogForm}>
          <TextField id="crm-task-title" autoFocus required label="Título" variant="outlined" size="small" value={form.title} onChange={change("title")} inputProps={{ maxLength: 160 }} disabled={saving} />
          <TextField id="crm-task-type" select label="Tipo de atividade" variant="outlined" size="small" value={form.type} onChange={change("type")} disabled={saving}>{Object.entries(types).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}</TextField>
          <TextField id="crm-task-owner" select required label="Responsável" variant="outlined" size="small" value={form.ownerId} onChange={change("ownerId")} disabled={saving || ownerLoading}>{owners.map(owner => <MenuItem key={owner.id} value={owner.id}>{owner.name}</MenuItem>)}</TextField>
          <TextField id="crm-task-deadline" required label="Data e hora" type="datetime-local" variant="outlined" size="small" InputLabelProps={{ shrink: true }} value={form.dueAt} onChange={change("dueAt")} disabled={saving} helperText="Horário deste dispositivo" />
          <TextField id="crm-task-description" label="Descrição (opcional)" multiline rows={3} variant="outlined" value={form.description} onChange={change("description")} inputProps={{ maxLength: 4000 }} disabled={saving} />
        </div>
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={saving}>Voltar</Button><Button type="submit" color="primary" variant="contained" disabled={saving || ownerLoading || !owners.length}>{saving ? "Salvando..." : "Criar retorno"}</Button></DialogActions>
    </form>
  </Dialog>;
}

export default function Tasks({ contactId, contactName, hideNumber = false, onCreate, onChanged, refreshKey = 0 }) {
  const classes = useStyles();
  const [view, setView] = useState("open");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState({ hasMore: false, nextCursor: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const sequence = useRef(0);
  const context = useRef(`${contactId || "all"}:${view}`);
  context.current = `${contactId || "all"}:${view}`;
  const load = useCallback(async afterId => {
    const request = ++sequence.current;
    setLoading(true);
    setError("");
    if (!afterId) { setItems([]); setPage({ hasMore: false, nextCursor: null }); }
    try {
      const { data } = await api.get("/crm/tasks", { params: { view, contactId: contactId || undefined, afterId: afterId || undefined } });
      if (request !== sequence.current) return;
      setItems(previous => afterId ? mergePages(previous, data.tasks) : data.tasks);
      setPage({ hasMore: data.hasMore, nextCursor: data.nextCursor });
    } catch (err) { if (request === sequence.current) setError(errorMessage(err)); }
    finally { if (request === sequence.current) setLoading(false); }
  }, [view, contactId]);
  useEffect(() => {
    load();
    return () => { sequence.current += 1; };
  }, [load, refreshKey]);
  useEffect(() => { setCreateOpen(false); }, [contactId]);
  const update = async (task, status) => {
    if (pending) return;
    const currentContext = context.current;
    setPending(task.id);
    setError("");
    try {
      await api.patch(`/crm/tasks/${task.id}`, { status, version: task.version });
      if (onChanged) onChanged();
      if (!alive.current || currentContext !== context.current) return;
      toast.success(status === "completed" ? "Retorno concluído." : "Retorno cancelado.");
      load();
    } catch (err) {
      if (err.response && err.response.status === 409 && onChanged) onChanged();
      if (!alive.current || currentContext !== context.current) return;
      if (err.response && err.response.status === 409) {
        await load();
        setError("O retorno foi alterado em outro dispositivo. Os dados atuais foram carregados; confira antes de tentar novamente.");
      } else setError(errorMessage(err));
    } finally { if (alive.current) setPending(null); }
  };
  return <Paper variant="outlined" className={classes.panel}>
    <div className={classes.header}>
      <Typography component="h2" className={classes.sectionTitle}>Retornos</Typography>
      <div className={classes.actions}>
        <Button startIcon={<Refresh />} onClick={() => load()} disabled={loading}>Atualizar</Button>
        {!onCreate && (contactId ? <Button startIcon={<Add />} color="primary" variant="contained" onClick={() => setCreateOpen(true)}>Novo retorno</Button> : <Button color="primary" component={RouterLink} to="/crm/relationship">Novo retorno</Button>)}
      </div>
    </div>
    <div className={classes.filters}><TextField id="crm-task-view" select label="Mostrar atividades" variant="outlined" size="small" value={view} disabled={Boolean(pending)} onChange={event => setView(event.target.value)}>{Object.entries(views).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}</TextField></div>
    {error && <Alert severity="error" className={classes.error} action={<Button onClick={() => load()}>Tentar novamente</Button>}>{error}</Alert>}
    {items.map(task => {
      const person = contactPresentation(task.contact || { id: task.contactId, name: task.contactName }, hideNumber);
      return <article key={task.id} className={classes.task}>
      <div className={classes.taskHeader}>
        <div className={classes.taskText}><Typography className={classes.eventHeading}>{task.title}</Typography>{!contactId && <Button size="small" component={RouterLink} to={`/crm/relationship/${task.contactId}`}>{person.name}{!person.hasName && person.reference ? ` · ${person.reference}` : ""}</Button>}</div>
        <Chip size="small" label={statuses[task.status] || task.status} color={task.status === "open" ? "primary" : "default"} variant="outlined" />
      </div>
      <Typography variant="body2">{types[task.type] || task.type} · {task.ownerName || "Responsável não informado"}</Typography>
      <Typography variant="body2" color={task.status === "open" && new Date(task.dueAt) < new Date() ? "error" : "textSecondary"}>Prazo: {formatDateTime(task.dueAt)}</Typography>
      {task.description && <Typography variant="body2" className={classes.eventBody}>{task.description}</Typography>}
      {task.completedAt && <Typography variant="caption" color="textSecondary">Concluído em {formatDateTime(task.completedAt)}</Typography>}
      {task.status === "open" && <div className={classes.actions}><Button color="primary" size="small" disabled={Boolean(pending)} onClick={() => update(task, "completed")}>{pending === task.id ? "Salvando..." : "Concluir"}</Button><Button size="small" disabled={Boolean(pending)} onClick={() => update(task, "cancelled")}>Cancelar retorno</Button></div>}
    </article>; })}
    {loading && <div className={classes.loading} role="status"><CircularProgress size={22} /><Typography>Carregando atividades...</Typography></div>}
    {!loading && !error && !items.length && <Typography className={classes.state}>Nenhuma atividade neste filtro.</Typography>}
    {page.hasMore && <div className={classes.pagination}><Button onClick={() => load(page.nextCursor)} disabled={loading}>Carregar mais atividades</Button></div>}
    {createOpen && <TaskForm contactId={contactId} contactName={contactName} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); if (onChanged) onChanged(); }} />}
  </Paper>;
}
